import { useQuery } from "@tanstack/react-query";
import { fetchLeafletEnquirySummary } from "@/api/leafletEnquiries";
import { useOnlineStatus } from "@/offline/useOnlineStatus";
import { LEAFLET_ENQUIRY_STATUS } from "./constants";

export const LEAFLET_ENQUIRY_SUMMARY_QUERY_KEY = [
  "leaflet-enquiries",
  "summary",
] as const;

export const LEAFLET_ENQUIRY_SUMMARY_REFETCH_MS = 90 * 1000;
export const OPEN_ASSIGNED_LEAFLET_ENQUIRY_SEARCH_EVENT =
  "assigned-leaflet-enquiry-search";

const MAX_ERROR_REFETCH_MS = 5 * 60 * 1000;

function isDocumentVisible() {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

function errorRefetchInterval(failureCount: number) {
  if (failureCount <= 0) return LEAFLET_ENQUIRY_SUMMARY_REFETCH_MS;
  return Math.min(30 * 1000 * 2 ** (failureCount - 1), MAX_ERROR_REFETCH_MS);
}

export function assignedLeafletEnquirySearchPath(userId: number | string) {
  const params = new URLSearchParams({
    status: LEAFLET_ENQUIRY_STATUS,
    owner: String(userId),
  });

  return `/search?${params.toString()}`;
}

export function assignedLeafletEnquiryLabel(count: number) {
  return count === 1
    ? "1 leaflet enquiry to follow up"
    : `${count} leaflet enquiries to follow up`;
}

export function notifyAssignedLeafletEnquirySearch(userId: number | string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(OPEN_ASSIGNED_LEAFLET_ENQUIRY_SEARCH_EVENT, {
      detail: { userId: String(userId) },
    })
  );
}

export function useLeafletEnquirySummary(
  enabled: boolean,
  options: { poll?: boolean } = {}
) {
  const online = useOnlineStatus();
  const poll = options.poll ?? false;

  return useQuery({
    queryKey: LEAFLET_ENQUIRY_SUMMARY_QUERY_KEY,
    queryFn: fetchLeafletEnquirySummary,
    enabled: enabled && online,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: poll ? "always" : false,
    refetchOnReconnect: poll ? "always" : false,
    refetchOnWindowFocus: poll ? "always" : false,
    refetchInterval: poll
      ? (query) => {
          if (!isDocumentVisible() || !online) return false;
          return errorRefetchInterval(query.state.fetchFailureCount);
        }
      : false,
    refetchIntervalInBackground: false,
    retry: 3,
    retryDelay: (attemptIndex) =>
      Math.min(30 * 1000 * 2 ** attemptIndex, MAX_ERROR_REFETCH_MS),
  });
}
